import THREE from "../Three";
import animations from "../../components/Animations";
import { BREAKPOINTS } from "../../styles/variables/Breakpoints";

export default class Heading {
  constructor(scene, manager) {
    this.scene = scene;
    this.camera = scene.userData.camera;
    this.element = scene.userData.element;
    this.container = document.body;
    this.manager = manager;
    this.id = 0;

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.intersecting = false;

    this.camera.position.z = 60;

    this.originalCameraPosition = new THREE.Vector3();
    this.originalCameraPosition.copy(this.camera.position);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(
      this.element.clientWidth,
      this.element.clientHeight,
      false
    );
    this.renderer.setClearColor(0x000000, 0);
    this.element.appendChild(this.renderer.domElement);

    this.controls = new THREE.OrbitControls(
      this.camera,
      this.renderer.domElement
    );

    this.isMobile = window.innerWidth < BREAKPOINTS.sm;
    this.isOrbitControlsActive = false;

    this.camera.position.copy(this.originalCameraPosition);

    this.onResize = this.onResize.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);

    this.settings();
    this.loadModel();
    this.createEventListeners();
  }

  settings() {
    if (this.controls) {
      this.controls.enablePan = false;
      this.controls.enableZoom = false;
      this.controls.enableDamping = true;

      this.controls.minPolarAngle = Math.PI / 9;
      this.controls.maxPolarAngle = (5 * Math.PI) / 9;

      this.controls.minAzimuthAngle = -Math.PI / 10;
      this.controls.maxAzimuthAngle = Math.PI / 10;

      this.controls.addEventListener("start", () => {
        this.isOrbitControlsActive = true;
      });
      this.controls.addEventListener("end", () => {
        this.isOrbitControlsActive = false;
        if (this.isMobile) {
          // this.returnCameraPosition(0.25);
        }
      });
    }

    this.pointLight1 = new THREE.PointLight(0xffffff, 5.4, 0, 0);
    this.pointLight1.position.set(500, 500, 500);
    this.scene.add(this.pointLight1);
  }

  loadModel() {
    const loader = new THREE.GLTFLoader(this.manager);

    const isHighsnobiety = window.location.href.includes("highsnobiety");

    const modelURL = isHighsnobiety
      ? "https://static.highsnobiety.com/interactive-stories/2023-09-lotus/models/LOTUS_Header.gltf"
      : "./models/LOTUS_Header.gltf";

    loader.load(modelURL, gltf => {
      this.heading = gltf.scene.children[0];
      this.scene.add(this.heading);

      this.heading.position.set(7, -5, 0);
      this.heading.scale.set(15, 15, 15);

      this.onResize();
    });
  }

  createEventListeners() {
    window.addEventListener("resize", this.onResize);

    if (!this.isMobile) {
      window.addEventListener("mousemove", this.onMouseMove);

      this.renderer.domElement.addEventListener(
        "mouseenter",
        this.onMouseEnter
      );
      this.renderer.domElement.addEventListener(
        "mouseleave",
        this.onMouseLeave
      );
    }
  }

  onMouseMove(event) {
    if (!this.isOrbitControlsActive && this.heading) {
      this.raycasterMouse(event);
    }
  }

  raycasterMouse(event) {
    if (this.isMobile) {
      return;
    }

    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    this.raycaster.layers.set(0);
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObject(this.heading);

    if (intersects.length > 0) {
      if (this.intersecting) return;

      this.intersecting = true;
      const intersectionPoint = intersects[0].point;
      const moveVector = new THREE.Vector3().subVectors(
        intersectionPoint,
        this.camera.position
      );

      moveVector.x = THREE.MathUtils.clamp(moveVector.x, -5, 5);
      moveVector.y = THREE.MathUtils.clamp(moveVector.y, -5, 5);
      moveVector.z = THREE.MathUtils.clamp(moveVector.z, -2, 2);

      const newCameraPosition = new THREE.Vector3().addVectors(
        this.camera.position,
        moveVector
      );

      const maxDistance = 8;
      const originalCameraPosition = this.originalCameraPosition.clone();
      const clampedCameraPosition = originalCameraPosition
        .clone()
        .add(
          newCameraPosition
            .clone()
            .sub(originalCameraPosition)
            .clampLength(0, maxDistance)
        );

      let animation = animations.to(this.camera.position, {
        duration: 0.5,
        x: clampedCameraPosition.x,
        y: clampedCameraPosition.y,
        z: clampedCameraPosition.z,
        ease: "power2.out",
        onUpdate: () => {
          if (this.isOrbitControlsActive) {
            animation.kill();
          }
        },
      });
    } else {
      this.intersecting = false;
    }
  }

  onMouseEnter(event) {
    if (this.isMobile) return;
    if (!this.isOrbitControlsActive && this.heading) {
      this.raycasterMouse(event);
    }
  }

  onMouseLeave() {
    if (this.isMobile || this.intersecting) return;
    if (!this.isOrbitControlsActive && this.heading) {
      this.returnCameraPosition(0);
    }
  }

  returnCameraPosition(delay) {
    animations.to(this.camera.position, {
      delay: delay,
      duration: 0.5,
      x: this.originalCameraPosition.x,
      y: this.originalCameraPosition.y,
      z: this.originalCameraPosition.z,
      ease: "power2.out",
    });
  }

  render() {
    if (this.controls) {
      this.controls.update();
    }
    if (this.heading) {
      this.heading.position.y += Math.sin(Date.now() * 0.002) * Math.PI * 0.003;
    }
    this.renderer.render(this.scene, this.camera);
  }

  onResize() {
    const aspectRatio = 1.6;
    const { innerWidth, innerHeight } = window;
    const currentAspectRatio = innerWidth / innerHeight;

    const scaleToFitWidth = factor => {
      this.camera.aspect = currentAspectRatio;
      this.camera.position.z = 60;
      this.heading.scale.set(factor, factor, factor);
      this.heading.position.set(4, 9, 0);
    };

    if (this.heading) {
      if (innerWidth < BREAKPOINTS.xxxs) {
        // 325
        scaleToFitWidth(40 * (currentAspectRatio / aspectRatio));
        this.heading.position.set(4, -12, 0);
      } else if (innerWidth < BREAKPOINTS.xxs) {
        // 390
        scaleToFitWidth(45 * (currentAspectRatio / aspectRatio));
        this.heading.position.set(4, -12, 0);
      } else if (innerWidth < BREAKPOINTS.xs) {
        // 480
        scaleToFitWidth(40 * (currentAspectRatio / aspectRatio));
        this.heading.position.set(4, -10, 0);
      } else if (innerWidth < BREAKPOINTS.sm) {
        // 768
        scaleToFitWidth(35 * (currentAspectRatio / aspectRatio));
        this.heading.position.set(6, -6, 0);
      } else if (innerWidth < BREAKPOINTS.md) {
        // 992
        scaleToFitWidth(24 * (currentAspectRatio / aspectRatio));
        this.heading.position.set(7, -4, 0);
      } else if (innerWidth < BREAKPOINTS.lg) {
        // 1024
        scaleToFitWidth(23 * (currentAspectRatio / aspectRatio));
        this.heading.position.set(7, -2, 0);
      } else if (innerWidth < BREAKPOINTS.xl) {
        // 1200
        scaleToFitWidth(22 * (currentAspectRatio / aspectRatio));
        this.heading.position.set(7, -2, 0);
      } else if (innerWidth < BREAKPOINTS.xxl) {
        // 1200 - 1600
        const maxScale = Math.min(21 * (currentAspectRatio / aspectRatio), 21);
        scaleToFitWidth(maxScale);
        this.heading.position.set(7, 0, 0);
      } else {
        // + 1600
        if (currentAspectRatio > aspectRatio) {
          const maxScale = Math.min(
            21 * (currentAspectRatio / aspectRatio),
            21
          );
          scaleToFitWidth(maxScale);
          this.heading.position.set(7, 0, 0);
        } else {
          scaleToFitWidth(21 * (aspectRatio / currentAspectRatio));
          this.heading.position.set(6, 0, 0);
        }
      }
    }

    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.element.clientWidth, this.element.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
  }
}
